import { useDialog } from '@/core/dialogsystem/useDialog';
import type { DialogQueueOptions } from '@/core/dialogsystem/dialogQueue';
import QuickEntryDialog from '@/core/observation/quick-entry/QuickEntryDialog.vue';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { computed, type ComputedRef } from 'vue';
import { EQuickEntryKind } from './quickEntry.enums';
import { type IQuickEntryDialogShowParams } from './quickEntry.utils';
import { useQuickEntrySupportMeasure } from './useQuickEntrySupportMeasure';

type TShowQuickEntryDialog = (params?: IQuickEntryDialogShowParams) => void;
type TShowQuickEntryDialogQueued = (
  queueOptions: DialogQueueOptions,
  params?: IQuickEntryDialogShowParams,
) => Promise<void | undefined>;

interface IQuickEntryDialogComposable {
  /** False without a current class. */
  isQuickEntryDialogAvailable: ComputedRef<boolean>;
  /** Opens the quick-entry composer as a centered desktop dialog or mobile fullscreen view. */
  showQuickEntryDialog: TShowQuickEntryDialog;
  /** Queues the quick-entry composer behind active dialogs. No-op while unavailable. */
  showQuickEntryDialogQueued: TShowQuickEntryDialogQueued;
}

export const useQuickEntryDialog = (): IQuickEntryDialogComposable => {
  const { currentClass } = useCurrentClass();
  const { requestSupportMeasureSelection } = useQuickEntrySupportMeasure();

  const quickEntryDialog = useDialog({ component: QuickEntryDialog, props: {} });

  const isQuickEntryDialogAvailable = computed(() => !!currentClass.value);

  const showQuickEntryDialog: TShowQuickEntryDialog = (params) => {
    if (!isQuickEntryDialogAvailable.value) return;
    const show = (): void => void quickEntryDialog.show(params ?? {});
    if (params?.kind === EQuickEntryKind.SupportMeasure) {
      requestSupportMeasureSelection(show);
      return;
    }
    show();
  };

  const showQuickEntryDialogQueued: TShowQuickEntryDialogQueued = (queueOptions, params) => {
    if (!isQuickEntryDialogAvailable.value) return Promise.resolve(undefined);
    const enqueue = (): Promise<void | undefined> =>
      quickEntryDialog.showQueued(
        {
          ...queueOptions,
          guard: () => isQuickEntryDialogAvailable.value && (queueOptions.guard?.() ?? true),
        },
        params ?? {},
      );
    if (params?.kind !== EQuickEntryKind.SupportMeasure) return enqueue();

    let queuedDialog = Promise.resolve<void | undefined>(undefined);
    requestSupportMeasureSelection(() => {
      queuedDialog = enqueue();
    });
    return queuedDialog;
  };

  return { isQuickEntryDialogAvailable, showQuickEntryDialog, showQuickEntryDialogQueued };
};
